import { jsPDF } from "jspdf";

const MARGIN = 18;
const PAGE_WIDTH = 210;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_wellness-upgrade-1/artifacts/sthkmul7_SDORA_Logo-copy.png";

let _logoBase64 = null;
async function loadLogo() {
  if (_logoBase64) return _logoBase64;
  try {
    const resp = await fetch(LOGO_URL);
    const blob = await resp.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => { _logoBase64 = reader.result; resolve(_logoBase64); };
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
}

function createBlankPdf() {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  let y = MARGIN;
  let pageNum = 1;

  const checkPage = (needed = 16) => {
    if (y + needed > 275) {
      addFooter();
      doc.addPage();
      pageNum++;
      y = MARGIN + 2;
    }
  };

  const addFooter = () => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(150);
    doc.text(`SDORA Wellbeing LLC — Intake & Consent Packet — Page ${pageNum}`, PAGE_WIDTH / 2, 290, { align: "center" });
    doc.setTextColor(0);
  };

  const sectionHeader = (text) => {
    checkPage(14);
    doc.setFillColor(30, 58, 95);
    doc.rect(MARGIN, y, CONTENT_WIDTH, 7, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(255);
    doc.text(text, MARGIN + 3, y + 5);
    doc.setTextColor(0);
    y += 10;
  };

  const blankLine = (label, width) => {
    checkPage(10);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(60);
    doc.text(label, MARGIN, y);
    const labelW = doc.getTextWidth(label + " ");
    const lineStart = MARGIN + labelW + 1;
    const lineEnd = width ? MARGIN + width : MARGIN + CONTENT_WIDTH;
    doc.setDrawColor(180);
    doc.line(lineStart, y, lineEnd, y);
    doc.setDrawColor(0);
    y += 7;
  };

  const blankLineRow = (pairs) => {
    checkPage(10);
    const colW = CONTENT_WIDTH / pairs.length;
    pairs.forEach(([label], i) => {
      const x = MARGIN + i * colW;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(60);
      doc.text(label, x, y);
      const labelW = doc.getTextWidth(label + " ");
      doc.setDrawColor(180);
      doc.line(x + labelW + 1, y, x + colW - 4, y);
      doc.setDrawColor(0);
    });
    y += 7;
  };

  const checkbox = (label) => {
    checkPage(8);
    doc.setDrawColor(100);
    doc.rect(MARGIN, y - 3, 3.5, 3.5);
    doc.setDrawColor(0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(40);
    doc.text(label, MARGIN + 5, y);
    y += 6;
  };

  const yesNoCheckbox = (label) => {
    checkPage(12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(40);
    doc.text(label, MARGIN, y);
    y += 5;
    doc.setDrawColor(100);
    doc.rect(MARGIN, y - 3, 3.5, 3.5);
    doc.setDrawColor(0);
    doc.text("Yes", MARGIN + 5, y);
    doc.setDrawColor(100);
    doc.rect(MARGIN + 20, y - 3, 3.5, 3.5);
    doc.setDrawColor(0);
    doc.text("No", MARGIN + 25, y);
    y += 6;
  };

  const textArea = (label, lines = 3) => {
    checkPage(8 + lines * 6);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(60);
    doc.text(label, MARGIN, y);
    y += 4;
    for (let i = 0; i < lines; i++) {
      doc.setDrawColor(200);
      doc.line(MARGIN, y, MARGIN + CONTENT_WIDTH, y);
      y += 6;
    }
    doc.setDrawColor(0);
    y += 2;
  };

  const note = (text) => {
    checkPage(8);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7);
    doc.setTextColor(120);
    const lines = doc.splitTextToSize(text, CONTENT_WIDTH);
    lines.forEach((line) => { doc.text(line, MARGIN, y); y += 3.5; });
    doc.setTextColor(0);
    doc.setFont("helvetica", "normal");
    y += 2;
  };

  return { doc, checkPage, addFooter, sectionHeader, blankLine, blankLineRow, checkbox, yesNoCheckbox, textArea, note, getY: () => y, setY: (v) => { y = v; }, getPage: () => pageNum };
}

export async function downloadBlankIntakePacket() {
  const logo = await loadLogo();
  const { doc, addFooter, sectionHeader, blankLine, blankLineRow, checkbox, yesNoCheckbox, textArea, note, checkPage, getY, setY } = createBlankPdf();

  // ── Page 1: Letterhead ──
  if (logo) {
    try { doc.addImage(logo, "PNG", MARGIN, MARGIN, 40, 18); } catch {}
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(30, 58, 95);
  doc.text("SDORA Wellbeing LLC", MARGIN + 46, MARGIN + 7);
  doc.setFontSize(9);
  doc.setTextColor(80);
  doc.setFont("helvetica", "normal");
  doc.text("1700 Reisterstown Rd, Suite 109, Pikesville, MD 21208", MARGIN + 46, MARGIN + 13);
  doc.text("Phone: (877) 772-5150  |  Fax: (667) 400-5108  |  sdorawellbeing.org", MARGIN + 46, MARGIN + 18);

  doc.setDrawColor(30, 58, 95);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, MARGIN + 22, MARGIN + CONTENT_WIDTH, MARGIN + 22);
  doc.setDrawColor(0);
  doc.setLineWidth(0.2);

  setY(MARGIN + 28);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(30, 58, 95);
  doc.text("Client Intake & Consent Packet", PAGE_WIDTH / 2, getY(), { align: "center" });
  setY(getY() + 5);
  doc.setFontSize(8);
  doc.setTextColor(100);
  doc.setFont("helvetica", "normal");
  doc.text("Please complete all sections. Fields marked with (*) are required.", PAGE_WIDTH / 2, getY(), { align: "center" });
  setY(getY() + 8);

  // ── Section 1: Personal Information ──
  sectionHeader("1. Personal Information");
  blankLineRow([["*First Name:"], ["Middle Name:"], ["*Last Name:"]]);
  blankLineRow([["Preferred Name:"], ["*Date of Birth:"]]);
  blankLineRow([["*Sex at Birth:"], ["Gender Identity:"]]);
  blankLine("Pronouns:");
  blankLine("*Street Address:");
  blankLineRow([["*City:"], ["*State:"], ["*ZIP:"]]);
  blankLineRow([["*Phone (XXX) XXX-XXXX:"], ["Email:"]]);
  blankLine("*Preferred Contact Method (Phone / Email / Text):");
  blankLineRow([["Marital Status:"], ["Race:"], ["Ethnicity:"]]);
  blankLine("*Language Preference:");
  blankLine("*Social Security Number (SSN):");
  note("SSN is required for billing and insurance verification. It will be encrypted and securely stored.");
  blankLine("Is the client a minor? (Yes / No):");
  blankLineRow([["Guardian Name:"], ["Guardian Phone (XXX) XXX-XXXX:"], ["Relationship:"]]);

  // ── Section 2: Emergency Contact ──
  sectionHeader("2. Emergency Contact");
  blankLine("*Emergency Contact Name:");
  blankLineRow([["*Relationship:"], ["*Phone (XXX) XXX-XXXX:"]]);
  blankLine("Alternate Phone (XXX) XXX-XXXX:");
  blankLine("*Permission to contact this person? (Yes / No):");

  // ── Section 3: Insurance ──
  sectionHeader("3. Insurance Information");
  blankLineRow([["*Insurance Carrier / MCO:"], ["*Member ID:"]]);
  blankLineRow([["Group Number:"], ["*Policyholder Name:"]]);
  blankLineRow([["Policyholder DOB:"], ["*Relationship to Policyholder:"]]);
  blankLine("Secondary Insurance? (Yes / No):");
  blankLineRow([["Secondary Carrier:"], ["Secondary Member ID:"]]);
  note("Please attach a copy of your insurance card (front and back) if available.");

  // ── Section 4: Service Information ──
  sectionHeader("4. Service Information & OMHC Requested Services");
  blankLine("How did you hear about SDORA?");
  blankLineRow([["Referring Provider:"], ["Referring Organization:"]]);
  blankLine("*Requested Service:");
  note("*Select at least one OMHC Requested Service:");
  checkbox("Psychiatric Evaluation");
  checkbox("Medication Management");
  checkbox("Individual Therapy");
  checkbox("Family Therapy");
  checkbox("Group Therapy");
  checkbox("Diagnostic Assessment");
  checkbox("Care Coordination");
  checkbox("Other (please specify): __________________________");
  textArea("*Reason for Seeking Services:", 3);
  blankLine("Preferred Service Format (In-person / Telehealth / No preference):");

  // ── Section 5: Presenting Concerns ──
  sectionHeader("5. Presenting Concerns");
  textArea("*Primary Concerns:", 3);
  textArea("Symptoms / Challenges:", 2);
  blankLine("When did these concerns begin?");
  blankLine("Current Level of Functioning:");
  textArea("Treatment Goals:", 2);
  textArea("Previous Behavioral Health Treatment:", 2);
  textArea("Previous Psychiatric Hospitalizations:", 2);

  // ── Section 6: Medical History ──
  sectionHeader("6. Medical / Clinical History");
  blankLine("Primary Care Provider:");
  textArea("*Current Diagnoses (enter 'None' if not applicable):", 2);
  textArea("Medical Conditions:", 2);
  blankLine("Allergies:");
  textArea("Current Medications:", 2);
  blankLine("Current Behavioral Health Providers:");
  textArea("Substance Use History:", 2);
  textArea("Relevant Hospitalization History:", 2);

  // ── Section 7: Safety Screening ──
  sectionHeader("7. Safety Screening");
  note("These questions help our team ensure your safety. If you are in immediate danger, call 911 or 988.");
  blankLine("*Currently having thoughts of suicide? (Yes / No):");
  blankLine("*Currently engaging in self-harm? (Yes / No):");
  blankLine("*Having thoughts of harming others? (Yes / No):");
  blankLine("*Had a recent psychiatric crisis? (Yes / No):");
  blankLine("*Feel at immediate safety risk? (Yes / No):");
  textArea("Additional safety details (optional):", 2);

  // ── Section 8: Social & Functional ──
  sectionHeader("8. Social & Functional Information");
  blankLine("Housing Status:");
  blankLine("Employment / School Status:");
  blankLine("Transportation Needs:");
  blankLine("Legal / Court Involvement:");
  blankLine("DJS Involvement:");
  textArea("Social / Support System:", 2);
  blankLine("Accessibility or Accommodation Needs:");

  // ── Section 9: Consents & Signatures ──
  sectionHeader("9. Consents & Acknowledgments");
  note("This section may be completed by the client (if an adult), or by a parent, legal guardian, authorized representative, agency representative/custodian, or court-appointed decision-maker on the client's behalf.");
  note("If the client is a minor, indicate who is signing below (Parent / Legal Guardian / Court-Appointed Guardian). If the client is an adult, indicate whether a legal representative applies.");
  blankLine("Signer Role (if minor):");
  blankLine("Legal Guardian / Representative Payee / POA? (if adult, Yes/No):");
  note("Please read each statement carefully and check the box to indicate your agreement. A plain-language note is included before each item; it does not replace or change the official wording.");
  note("What this means: Agreeing to receive OMHC and/or PRP services from SDORA Wellbeing.");
  yesNoCheckbox("Item 1: I consent to receive treatment and services from SDORA Wellbeing LLC.");
  note("What this means: Confirms you received SDORA's HIPAA privacy notice explaining how your health information is protected.");
  checkbox("Item 2: I acknowledge I have received and reviewed the Notice of Privacy Practices.");
  note("What this means: Allows SDORA to reach you by email, text, or patient portal for reminders and forms.");
  yesNoCheckbox("Item 3: I consent to communication via electronic means (email, text, patient portal).");
  note("What this means: For OMHC, telehealth may be used when clinically appropriate. For PRP, Maryland COMAR requires an in-person assessment first; telehealth is used afterward only when appropriate.");
  yesNoCheckbox("Item 4: I acknowledge the telehealth services policy (OMHC telehealth, or PRP telehealth limitations per Maryland COMAR).");
  note("What this means: Allows SDORA staff to contact your emergency contact if there is ever a safety concern.");
  yesNoCheckbox("Item 5: I authorize SDORA Wellbeing to contact my emergency contact when necessary for my safety.");
  note("What this means: You understand you (or the policyholder) are responsible for costs insurance does not cover.");
  checkbox("Item 6: I acknowledge and accept financial/insurance responsibility for services rendered.");
  note("What this means: Confirms you know how to file a complaint with SDORA and that you may also file with the Maryland Behavioral Health Administration.");
  checkbox("Item 7: I acknowledge I have received and reviewed SDORA Wellbeing's Grievance and Complaint Procedure, including my right to file a complaint with the Maryland Behavioral Health Administration.");
  note("What this means: Confirms you received a written explanation of your rights and responsibilities as a client.");
  checkbox("Item 8: I acknowledge I have received and reviewed my Client Rights & Responsibilities.");

  checkPage(20);
  note("Initial each item below. The person signing this packet initials: the client, or the parent or legal guardian signing for the client.");

  checkPage(20);
  note("What this means: SDORA can provide services onsite (our office), offsite (e.g. your home/school), or blended (a mix). This applies to OMHC and/or PRP services.");
  note("Item 9: I was offered onsite services, offsite services, and blended services, and the differences were explained to me. I choose:");
  checkbox("Onsite only");
  checkbox("Offsite only");
  checkbox("Blended");
  blankLine("*Initials:", 30);

  checkPage(20);
  note("What this means: PRP sessions build daily-living, coping, and social skills through structured activities. This requirement applies only to PRP services, not OMHC-only services.");
  note("Item 10 - ABOUT PSYCHIATRIC REHABILITATION SERVICES: Psychiatric Rehabilitation (PRP) is a clinical skills-training program. It is not a treatment program, a social program, a childcare program, or a mentoring program.");
  checkbox("This was explained to me and I received it in writing. (Required only if PRP services were requested; otherwise mark N/A.)");
  checkbox("Not applicable - no PRP services selected");
  blankLine("Initials (if applicable):", 30);

  checkPage(20);
  note("What this means: Records whether you want SDORA staff to provide/arrange transportation to PRP-related appointments and activities. If the client is a minor, a parent or guardian must authorize this.");
  note("Item 11 - TRANSPORTATION AUTHORIZATION: I authorize SDORA Wellbeing staff to provide or arrange transportation for program-related activities and appointments. If the client is a minor, this authorization must be signed by the parent, legal guardian, or court-appointed guardian.");
  checkbox("Yes, I authorize transportation");
  checkbox("No, I decline transportation services");
  blankLine("*Initials:", 30);

  checkPage(20);
  note("What this means: You can say no to treatment at any time, for OMHC or PRP, without losing access to other services, except where required by law.");
  checkbox("Item 12: I acknowledge that I have the right to refuse treatment at any time, and that refusal will not affect my eligibility for other services, except as required by law (e.g., court-ordered treatment).");

  checkPage(20);
  note("Release of Information: If you would like SDORA Wellbeing to share information with a specific provider or agency, please describe below. This is optional and applies to OMHC and/or PRP coordination of care.");
  textArea("Release of Information Details:", 2);

  // ── Signature Block ──
  checkPage(45);
  sectionHeader("Electronic / Written Signature");
  blankLine("*Signature:");
  blankLine("*Printed Name:");
  blankLine("Relationship to Client (if guardian/representative):");
  note("Consent Effective Date is the date this consent applies to (usually today's date). The Electronically Signed / Signed Date below is recorded separately and reflects when this form was actually signed.");
  blankLine("*Consent Effective Date:");
  blankLine("Reason for Earlier Effective Date (required only if the Consent Effective Date above is earlier than the Signed Date):");
  blankLine("*Signed Date:");
  note("By signing above, you confirm that the information provided is accurate to the best of your knowledge and that you agree to the consents acknowledged above.");

  checkPage(20);
  note("RETURN THIS COMPLETED FORM: Bring it to your first appointment at SDORA Wellbeing LLC, 1700 Reisterstown Rd, Suite 109, Pikesville, MD 21208, or upload it securely at sdorawellbeing.org/intake.");

  addFooter();
  doc.save("SDORA_Wellbeing_Intake_Consent_Packet.pdf");
}

export async function downloadPRPInfoSheet() {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  let y = MARGIN;

  const logo = await loadLogo();
  if (logo) {
    try { doc.addImage(logo, "PNG", MARGIN, y, 40, 14); } catch { /* skip logo on failure */ }
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(60);
  doc.text("SDORA Wellbeing LLC", PAGE_WIDTH - MARGIN, y + 5, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("1700 Reisterstown Rd, Suite 109, Pikesville, MD 21208", PAGE_WIDTH - MARGIN, y + 10, { align: "right" });
  y += 22;

  doc.setDrawColor(30, 58, 95);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
  y += 10;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(30, 58, 95);
  doc.text("About Psychiatric Rehabilitation (PRP) Services", MARGIN, y);
  y += 10;
  doc.setTextColor(0);

  const paragraph = (text, size = 10, gap = 7) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(text, CONTENT_WIDTH);
    doc.text(lines, MARGIN, y);
    y += lines.length * (size / 2) + gap;
  };

  const heading = (text) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(30, 58, 95);
    doc.text(text, MARGIN, y);
    doc.setTextColor(0);
    y += 6;
  };

  heading("What is PRP?");
  paragraph(
    "Psychiatric Rehabilitation (PRP) is a clinical skills-training program. It is not a treatment program, " +
    "a social program, a childcare program, or a mentoring program."
  );

  heading("What This Means for You");
  paragraph(
    "PRP sessions focus on building practical daily-living, social, and coping skills through structured, " +
    "goal-oriented activities guided by our clinical team. PRP is designed to work alongside — not replace — " +
    "any ongoing therapy or medication management you may be receiving."
  );

  heading("Service Delivery Options");
  paragraph(
    "SDORA Wellbeing offers PRP services onsite, offsite, or through a blended combination of both, depending " +
    "on your individualized rehabilitation plan and needs."
  );

  y += 4;
  doc.setDrawColor(200);
  doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
  y += 8;

  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  doc.setTextColor(90);
  doc.text("Questions about PRP services? Contact SDORA Wellbeing at (877) 772-5150.", MARGIN, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(150);
  doc.text("SDORA Wellbeing LLC — About PRP Services", PAGE_WIDTH / 2, 290, { align: "center" });

  doc.save("SDORA_Wellbeing_About_PRP_Services.pdf");
}
